import { Link } from "react-router-dom";
import StatusPill from "shared/StatusPill";
import type { Tone } from "shared/tones";
import type { CompanyActiveOutreach, CompanyOutreachDetail, OutreachPhase } from "../api/OutreachTouchesApi";
import { formatCalendarDate } from "../outreachDates";
import { CompanyTimeline } from "./OutreachTimeline";

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

const PHASE_TONE: Record<OutreachPhase, Tone> = { waiting_start: "neutral", in_progress: "brand", replied: "success", cooldown: "warning" };

/** The one-line status of a company's outreach: the pill says where it is, the line under it says which RFP and until when it is locked. */
export function outreachHeadline(o: CompanyActiveOutreach): string {
  switch (o.phase) {
    case "replied": return "Replied";
    case "waiting_start": return "Drafted, waiting for the campaign to start";
    case "cooldown": return `Sequence finished, cooling down`;
    default: return o.next_step ? `${o.next_step.label}${o.next_step.date ? ` · ${formatCalendarDate(o.next_step.date, false)}` : ""}` : "In progress, waiting on the contact";
  }
}

/** Companies table cell: concise status of the company's active outreach. */
export function CompanyOutreachCell({ outreach, historyCount }: { outreach?: CompanyActiveOutreach | null; historyCount?: number }) {
  if (!outreach) {
    return (
      <span className="text-xs text-muted-foreground" data-testid="outreach-none">
        {historyCount ? `Free to contact · ${historyCount} earlier campaign${historyCount === 1 ? "" : "s"}` : "—"}
      </span>
    );
  }
  return (
    <div className="space-y-0.5" data-testid="outreach-active">
      <StatusPill tone={PHASE_TONE[outreach.phase]} size="sm">{outreachHeadline(outreach)}</StatusPill>
      <div className="text-[11px] text-muted-foreground">
        <Link to={`${crmBase()}?opportunity_id=${outreach.opportunity_id}`} className="underline underline-offset-2">{outreach.title}</Link>
        {" · locked until "}{formatCalendarDate(outreach.locked_until, false)}
      </div>
    </div>
  );
}

/** Company page: the full timeline of the active outreach, with earlier campaigns underneath. */
export function CompanyOutreachSection({ outreach }: { outreach?: CompanyOutreachDetail }) {
  if (!outreach) return null;
  const { active, history } = outreach;
  return (
    <div data-testid="company-outreach">
      <p className="text-xs font-medium text-muted-foreground mb-2">Outreach</p>
      <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
        {active ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone={PHASE_TONE[active.phase]} size="sm">{outreachHeadline(active)}</StatusPill>
              <Link to={`${crmBase()}?opportunity_id=${active.opportunity_id}`} className="text-sm text-primary hover:underline">{active.title}</Link>
            </div>
            {active.timeline && <CompanyTimeline company={active.timeline} drafted />}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Not in an active campaign: free to contact.</p>
        )}
        {history.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Earlier campaigns ({history.length})</summary>
            <ul className="mt-2 space-y-1 text-sm">
              {history.map((h) => (
                <li key={h.opportunity_id} className="flex flex-wrap items-center gap-2">
                  <Link to={`${crmBase()}?opportunity_id=${h.opportunity_id}`} className="text-primary hover:underline">{h.title}</Link>
                  <span className="text-xs text-muted-foreground">
                    {h.started_on ? `started ${formatCalendarDate(h.started_on, false)}` : h.drafted_on ? `drafted ${formatCalendarDate(h.drafted_on, false)}` : ""}
                  </span>
                  {h.replied && <StatusPill tone="success" size="sm">replied</StatusPill>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
